import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Empty } from '@/components/ui/Empty';
import { formatTime12 } from '@/lib/time';
import type { Class } from '@pirouette/shared';

export const Route = createFileRoute('/faculty/classes')({
  component: FacultyClasses,
});

// "My classes" — faculty's RLS scoping (Sprint 2 §4) lets a select on
// classes + class_instructors join, but the policy for `classes` is broad
// (all authenticated SELECT). We narrow it client-side by joining against
// class_instructors → instructors → auth.uid().
function FacultyClasses() {
  const location = useLocation();
  const currentPath = location.pathname.replace(/\/+$/, '') || '/';
  // Unit tests mount route components at `/`; the real app mounts this list at
  // `/faculty/classes` and nested child pages below it.
  const isClassesIndex = currentPath === '/' || currentPath === '/faculty/classes';
  const myClasses = useQuery({
    enabled: isClassesIndex,
    queryKey: ['faculty-classes'],
    queryFn: async (): Promise<Class[]> => {
      const userResponse = await supabase.auth.getUser();
      const userId = userResponse.data.user?.id;
      if (!userId) return [];

      // First: resolve the instructor row for the logged-in faculty user.
      const { data: instructorRow, error: e1 } = await supabase
        .from('instructors')
        .select('id')
        .eq('auth_user_id', userId)
        .maybeSingle();
      if (e1) throw e1;
      if (!instructorRow) return [];

      // Then: classes joined via class_instructors.
      const { data, error } = await supabase
        .from('class_instructors')
        .select('classes(*)')
        .eq('instructor_id', instructorRow.id);
      if (error) throw error;

      interface Row {
        classes: Class | null;
      }
      return ((data ?? []) as Row[])
        .map((r) => r.classes)
        .filter((c): c is Class => c !== null && c.deleted_at === null);
    },
  });

  if (!isClassesIndex) {
    return <Outlet />;
  }

  return (
    <div className="space-y-8">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Teaching
        </p>
        <h1 className="mt-2">My classes</h1>
      </header>

      {myClasses.isLoading && <p aria-live="polite">Loading…</p>}

      {myClasses.data?.length === 0 && (
        <Empty
          title="No classes assigned"
          description="Ask the studio admin to add you to a class."
        />
      )}

      {myClasses.data && myClasses.data.length > 0 && (
        <ul className="grid gap-3">
          {myClasses.data.map((c) => (
            <li key={c.id} className="surface-elevated p-4">
              <Link
                to="/faculty/classes/$id/attendance"
                params={{ id: c.id }}
                className="flex flex-wrap items-baseline justify-between gap-2"
              >
                <span>
                  <span className="font-medium">{c.name}</span>
                  <span className="ml-3 text-[var(--color-ink-muted)] text-[var(--text-sm)] capitalize">
                    {c.weekday} {formatTime12(c.start_time)}–{formatTime12(c.end_time)} · {c.room}
                  </span>
                </span>
                <span className="text-[var(--color-accent)] text-[var(--text-sm)]">
                  Mark attendance →
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
